/**
 * Formulaire de vérification 2FA (VERSION CORRIGÉE)
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire pour saisir le code d'authentification à deux facteurs.
 * Supporte les codes numériques (6 chiffres) ET les codes backup alphanumériques (format: 651B-1KP0-UMRE)
 */

  // Client Component pour Next.js

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { ButtonSpinner } from '../ui/LoadingSpinner';
import { twoFactorApi } from '../../../infrastructure/api/twoFactorApi';

/**
 * Props du composant TwoFactorForm
 */
export interface TwoFactorFormProps {
  /** ID de la session 2FA temporaire */
  sessionId?: string;
  /** ID de l'utilisateur (fallback si sessionId pas disponible) */
  userId?: string;
  /** Méthode 2FA active (EMAIL ou APPLICATION) */
  method?: 'EMAIL' | 'APPLICATION';
  /** Callback après vérification réussie */
  onSuccess?: () => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Callback pour utiliser un code de backup */
  onUseBackupCode?: () => void;
  /** Callback pour annuler le processus 2FA */
  onCancel?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant TwoFactorForm
 *
 * @example
 * ```tsx
 * function TwoFactorPage() {
 *   const { twoFactorSessionData } = useAuth();
 *
 *   return (
 *     <TwoFactorForm
 *       userId={twoFactorSessionData?.idUtilisateur}
 *       method={twoFactorSessionData?.methode2FA}
 *       onSuccess={() => navigate('/dashboard')}
 *     />
 *   );
 * }
 * ```
 */
export function TwoFactorForm({
  sessionId,
  userId,
  method = 'APPLICATION',
  onSuccess,
  onError,
  onUseBackupCode,
  onCancel,
  className = '',
}: TwoFactorFormProps) {
  // Selecteurs Zustand individuels : evite les re-renders en cascade
  // (useAuth() retourne un nouvel objet a chaque render sans useMemo,
  // et useAuthStore() bare s'abonne a tout le store → detachement DOM des inputs)
  const verify2FA = useAuthStore((s) => s.verify2FA);
  const verifyBackupCode = useAuthStore((s) => s.verifyBackupCode);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);
  const twoFactorSessionData = useAuthStore((s) => s.twoFactorSessionData);

  // Utiliser sessionId du prop, sinon du store
  const effectiveSessionId = sessionId || twoFactorSessionData?.sessionId;
  const effectiveUserId = userId;

  // Mapper la méthode vers les valeurs attendues par l'API
  const mapMethod = (m?: 'EMAIL' | 'APPLICATION'): '2FA_EMAIL' | '2FA_TOTP' => {
    if (m === 'EMAIL') return '2FA_EMAIL';
    return '2FA_TOTP';  // Par défaut APPLICATION → 2FA_TOTP
  };

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [backupCode, setBackupCode] = useState('');
  const [isBackupMode, setIsBackupMode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const backupInputRef = useRef<HTMLInputElement | null>(null);

  // Focus sur le premier input au montage
  useEffect(() => {
    if (isBackupMode) {
      backupInputRef.current?.focus();
    } else {
      inputRefs.current[0]?.focus();
    }
  }, [isBackupMode]);

  // Timer cooldown pour le renvoi de code
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Handler renvoi de code EMAIL
  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0 || !effectiveUserId) return;
    try {
      await twoFactorApi.sendCode(effectiveUserId);
      setResendMessage('Code renvoyé avec succès');
      setResendCooldown(60);
      setTimeout(() => setResendMessage(null), 3000);
    } catch {
      setResendMessage('Erreur lors du renvoi du code');
      setTimeout(() => setResendMessage(null), 3000);
    }
  }, [resendCooldown, effectiveUserId]);

  /**
   * Gérer le changement d'un digit (mode normal)
   */
  const handleChange = (index: number, value: string) => {
    // Accepter uniquement les chiffres
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Prendre seulement le dernier caractère
    setCode(newCode);

    // Auto-focus sur le prochain input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit si tous les champs sont remplis
    if (newCode.every((digit) => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  /**
   * Gérer le changement du code backup
   */
  const handleBackupCodeChange = (value: string) => {
    // Accepter alphanumériques et tirets, convertir en majuscules
    const formatted = value
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, '')
      .slice(0, 14); // Format: 651B-1KP0-UMRE (14 caractères avec tirets)

    setBackupCode(formatted);

    // Auto-submit si le format est complet (12 caractères sans tirets = 14 avec tirets)
    const withoutDashes = formatted.replace(/-/g, '');
    if (withoutDashes.length === 12) {
      handleSubmit(withoutDashes);
    }
  };

  /**
   * Gérer la touche backspace
   */
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Gérer le paste (mode normal)
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
    setCode(newCode);

    // Focus sur le dernier input rempli
    const lastFilledIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastFilledIndex]?.focus();

    // Auto-submit si 6 digits
    if (pastedData.length === 6) {
      handleSubmit(pastedData);
    }
  };

  /**
   * Gérer le paste du code backup
   */
  const handleBackupPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, '')
      .slice(0, 14);

    setBackupCode(pastedData);

    // Auto-submit si le format est complet
    const withoutDashes = pastedData.replace(/-/g, '');
    if (withoutDashes.length === 12) {
      handleSubmit(withoutDashes);
    }
  };

/**
   * Gérer la soumission
   */
  const handleSubmit = async (codeValue?: string) => {
    clearError();

    const finalCode = codeValue || (isBackupMode ? backupCode.replace(/-/g, '') : code.join(''));

    // Vérifier la longueur selon le mode
    const expectedLength = isBackupMode ? 12 : 6;
    if (finalCode.length !== expectedLength) {
      return;
    }

    // Vérifier qu'on a un sessionId (pour 2FA normal) ou userId (pour backup)
    if (!isBackupMode && !effectiveSessionId) {
      onError?.('Session 2FA invalide. Veuillez vous reconnecter.');
      return;
    }
    if (isBackupMode && !effectiveUserId) {
      onError?.('Identifiant utilisateur manquant. Veuillez vous reconnecter.');
      return;
    }

    try {
      if (isBackupMode) {
        // Utiliser la fonction spécifique pour les codes backup
        // Le backend attend idUtilisateur (UUID) + code (12 alphanumériques)
        await verifyBackupCode({
          idUtilisateur: effectiveUserId!,
          code: finalCode,
        });
      } else {
        // Utiliser la fonction normale pour les codes 2FA
        await verify2FA({
          sessionId: effectiveSessionId!,
          code: finalCode,
          methode: mapMethod(method),
        });
      }
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Code invalide';
      onError?.(message);
      // Réinitialiser le code en cas d'erreur
      if (isBackupMode) {
        setBackupCode('');
        backupInputRef.current?.focus();
      } else {
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    }
  };
  /**
   * Basculer en mode backup code
   */
  const toggleBackupMode = () => {
    setIsBackupMode(!isBackupMode);
    setCode(['', '', '', '', '', '']);
    setBackupCode('');
    clearError();
  };

  /**
   * Soumettre manuellement (pour le mode backup)
   */
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <div className={`w-full max-w-md ${className}`}>
      <form onSubmit={handleManualSubmit} className="space-y-6">
        {/* Titre */}
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-accent-light rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-content">
            Authentification à deux facteurs
          </h2>
          <p className="mt-2 text-sm text-content-muted">
            {isBackupMode
              ? 'Entrez un code de backup (format: 651B-1KP0-UMRE)'
              : method === 'EMAIL'
              ? 'Entrez le code reçu par email'
              : 'Entrez le code de votre application d\'authentification'}
          </p>
        </div>

        {/* Erreur */}
        {error && (
          <div
            className="p-4 bg-danger-light border border-danger rounded-lg"
            role="alert"
          >
            <p className="text-sm text-danger text-center">{error}</p>
          </div>
        )}

        {/* Inputs du code */}
        <div>
          {isBackupMode ? (
            // Mode backup : input unique
            <div className="space-y-4">
              <input
                ref={backupInputRef}
                type="text"
                value={backupCode}
                onChange={(e) => handleBackupCodeChange(e.target.value)}
                onPaste={handleBackupPaste}
                placeholder="651B-1KP0-UMRE"
                disabled={isLoading}
                className="w-full h-14 px-4 text-center text-xl font-mono tracking-widest border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed bg-surface text-content placeholder-content-subtle"
                aria-label="Code de backup"
              />
              <button
                type="submit"
                disabled={isLoading || backupCode.replace(/-/g, '').length !== 12}
                className="w-full py-3 px-4 bg-primary hover:bg-primary-hover disabled:opacity-50 text-content-inverted font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                {isLoading ? 'Vérification...' : 'Vérifier le code'}
              </button>
            </div>
          ) : (
            // Mode normal : 6 inputs séparés
            <div className="flex justify-center gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isLoading}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed bg-surface text-content"
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>
          )}

          {isLoading && (
            <div className="mt-4 flex items-center justify-center">
              <ButtonSpinner className="mr-2" />
              <span className="text-sm text-content-muted">
                Vérification en cours...
              </span>
            </div>
          )}
        </div>

        {/* Bouton backup code */}
        <div className="text-center">
          <button
            type="button"
            onClick={toggleBackupMode}
            disabled={isLoading}
            className="text-sm font-medium text-primary-text hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBackupMode
              ? 'Utiliser le code de l\'application'
              : 'Utiliser un code de backup'}
          </button>
        </div>

        {/* Info backup codes */}
        {isBackupMode && (
          <div className="p-3 bg-accent-light border border-accent rounded-md">
            <p className="text-xs text-accent">
              Les codes de backup sont des codes alphanumériques à usage unique (format: 651B-1KP0-UMRE)
              que vous avez sauvegardés lors de l'activation de l'authentification à deux facteurs.
            </p>
          </div>
        )}

        {/* Message d'aide + Renvoyer le code */}
        {!isBackupMode && method === 'EMAIL' && (
          <div className="text-center space-y-1">
            {resendMessage && (
              <p className={`text-xs ${resendMessage.includes('succès') ? 'text-success' : 'text-danger'}`}>
                {resendMessage}
              </p>
            )}
            <p className="text-xs text-content-subtle">
              Vous n'avez pas reçu de code ?{' '}
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || isLoading}
                className="text-primary-text hover:text-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Renvoyer le code (${resendCooldown}s)` : 'Renvoyer le code'}
              </button>
            </p>
          </div>
        )}

        {/* Bouton annulation */}
        {onCancel && (
          <div className="mt-6 text-center pt-4 border-t border-border">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="text-sm text-content-muted hover:text-content disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Annuler et retour à la connexion
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
